import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, User, Mail, Phone, Globe, Building2, Briefcase, MapPin, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_ICONS = {
  email: { color: '#8B5CF6', icon: null, label: 'Email' },
  instagram: { color: '#E4405F', icon: 'https://cdn.simpleicons.org/instagram/E4405F', label: 'Instagram' },
  facebook: { color: '#1877F2', icon: 'https://cdn.simpleicons.org/facebook/1877F2', label: 'Facebook' },
  x: { color: '#000000', icon: 'https://cdn.simpleicons.org/x/000000', label: 'X' },
  linkedin: { color: '#0A66C2', icon: 'https://cdn.simpleicons.org/linkedin/0A66C2', label: 'LinkedIn' },
  youtube: { color: '#FF0000', icon: 'https://cdn.simpleicons.org/youtube/FF0000', label: 'YouTube' },
  tiktok: { color: '#000000', icon: 'https://cdn.simpleicons.org/tiktok/000000', label: 'TikTok' },
  github: { color: '#181717', icon: 'https://cdn.simpleicons.org/github/181717', label: 'GitHub' },
  whatsapp: { color: '#25D366', icon: 'https://cdn.simpleicons.org/whatsapp/25D366', label: 'WhatsApp' },
  whatsapp_business: { color: '#25D366', icon: 'https://cdn.simpleicons.org/whatsapp/25D366', label: 'WhatsApp Business' },
  whatsapp_community: { color: '#25D366', icon: 'https://cdn.simpleicons.org/whatsapp/25D366', label: 'WhatsApp Community' },
  telegram: { color: '#26A5E4', icon: 'https://cdn.simpleicons.org/telegram/26A5E4', label: 'Telegram' },
  snapchat: { color: '#FFFC00', icon: 'https://cdn.simpleicons.org/snapchat/FFFC00', label: 'Snapchat' },
  discord: { color: '#5865F2', icon: 'https://cdn.simpleicons.org/discord/5865F2', label: 'Discord' },
  threads: { color: '#000000', icon: 'https://cdn.simpleicons.org/threads/000000', label: 'Threads' },
};

const getSocialHref = (url) => {
  if (!url) return '#';
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  return `https://${url}`;
};

const SignaturePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const res = await axios.get(`${API}/api/u/${username}/signature`);
        setData(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchSignature();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Signature not found</h1>
          <p className="text-[#71717A] mb-6">This profile doesn't exist</p>
          <Button onClick={() => navigate('/')} className="bg-violet-600 hover:bg-violet-700 rounded-xl">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const accent = data.theme_color || '#8B5CF6';
  const socials = (data.social_links || []).filter(l => SOCIAL_ICONS[l.platform]);
  const profileUrl = `${API}/u/${username}`;

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04]"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }} />

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-12 sm:py-20">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10">
          <p className="text-xs font-medium tracking-widest uppercase mb-3" style={{ color: accent }}>
            Digital Business Card
          </p>
        </motion.div>

        {/* Signature Card */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="bg-[#121212] rounded-2xl border border-[#27272A] overflow-hidden mb-8"
          data-testid="signature-card">

          {/* Accent top bar */}
          <div className="h-1.5" style={{ background: accent }} />

          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-5">
              {data.avatar_url && (
                <img src={resolveMediaUrl(data.avatar_url)} alt={data.display_name}
                  className="w-20 h-20 rounded-full object-cover border-2 flex-shrink-0"
                  style={{ borderColor: accent }} />
              )}
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-white" data-testid="sig-name">{data.display_name}</h1>
                {data.tagline && <p className="text-sm mt-1 font-medium" style={{ color: accent }}>{data.tagline}</p>}
                {data.business_name && <p className="text-sm text-[#A1A1AA] mt-0.5">{data.business_name}</p>}
              </div>
            </div>

            {/* Contact Details */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.email && (
                <a href={`mailto:${data.email}`} className="flex items-center gap-2.5 text-sm text-[#A1A1AA] hover:text-white transition-colors group">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${accent}15` }}>
                    <Mail className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <span className="truncate">{data.email}</span>
                </a>
              )}
              {data.phone && (
                <a href={`tel:${data.phone}`} className="flex items-center gap-2.5 text-sm text-[#A1A1AA] hover:text-white transition-colors">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${accent}15` }}>
                    <Phone className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <span>{data.phone}</span>
                </a>
              )}
              {data.website && (
                <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[#A1A1AA] hover:text-white transition-colors">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${accent}15` }}>
                    <Globe className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <span className="truncate">{data.website}</span>
                </a>
              )}
              {data.address && (
                <div className="flex items-center gap-2.5 text-sm text-[#A1A1AA]">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: `${accent}15` }}>
                    <MapPin className="w-4 h-4" style={{ color: accent }} />
                  </div>
                  <span className="truncate">{data.address}</span>
                </div>
              )}
            </div>

            {/* Social Links */}
            {socials.length > 0 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {socials.map(s => {
                  const info = SOCIAL_ICONS[s.platform];
                  const url = getSocialHref(s.url);
                  return (
                    <a key={s.id} href={url} target="_blank" rel="noopener noreferrer"
                      className="w-9 h-9 rounded-lg flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors"
                      title={info.label}>
                      {info.icon
                        ? <img src={info.icon} alt={info.label} className="w-4 h-4" />
                        : <Mail className="w-4 h-4" style={{ color: info.color }} />}
                    </a>
                  );
                })}
              </div>
            )}

            {/* View Full Profile */}
            <div className="mt-6 pt-5 border-t border-[#27272A]">
              <a href={`/${username}`} data-testid="view-profile-link"
                className="inline-flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: accent }}>
                <ExternalLink className="w-4 h-4" /> View full One Link profile
              </a>
            </div>
          </div>
        </motion.div>

        {/* CTA: Create Your Own */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="bg-gradient-to-br from-[#1a1a2e] to-[#121212] rounded-2xl border border-[#27272A] p-6 sm:p-8 text-center"
          data-testid="create-own-cta">

          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: `${accent}20` }}>
            <Sparkles className="w-6 h-6" style={{ color: accent }} />
          </div>

          <h2 className="text-xl font-bold text-white mb-2">Create your own One Link</h2>
          <p className="text-[#71717A] text-sm mb-6 max-w-md mx-auto">
            Build your digital business identity in minutes. Share your links, products, bookings, and more — all from one page.
          </p>

          <Button data-testid="create-own-btn"
            onClick={() => navigate('/auth')}
            className="rounded-xl h-12 px-8 font-semibold text-base"
            style={{ background: accent }}>
            Get Started Free <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-[#525252] text-xs mt-4">No credit card required</p>
        </motion.div>

        {/* Footer */}
        <div className="text-center mt-8">
          <a href="/" className="text-xs text-[#525252] hover:text-[#71717A] transition-colors">
            Powered by <span className="font-semibold">One Link</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default SignaturePage;
